<template>
  <BaseModal :is-open="isOpen" title="How to Play Canasta" max-width="800px" @close="handleClose">
    <div class="how-to-play-container">
      <!-- Tab Navigation -->
      <div class="tab-nav">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="tab-button"
          :class="{ active: activeTab === tab.id }"
        >
          <span class="material-symbols-outlined">{{ tab.icon }}</span>
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" class="tab-panel">
          <h3 class="panel-title">Welcome to Hand and Foot Canasta – <em>Hooley Style</em></h3>

          <!-- The Basics -->
          <div class="overview-section basics-section">
            <div class="section-header">
              <span class="material-symbols-outlined">groups</span>
              <h4>The Basics</h4>
            </div>
            <div class="basics-grid">
              <div class="basic-item">
                <span class="basic-label">Players</span>
                <span class="basic-value">4 players, 2 teams</span>
              </div>
              <div class="basic-item">
                <span class="basic-label">Partners</span>
                <span class="basic-value">Sit diagonally across</span>
              </div>
              <div class="basic-item">
                <span class="basic-label">Cards</span>
                <span class="basic-value">4 decks (216 cards)</span>
              </div>
              <div class="basic-item">
                <span class="basic-label">Goal</span>
                <span class="basic-value">First to 10,000 points</span>
              </div>
            </div>
          </div>

          <!-- Your Two Hands -->
          <div class="overview-section hands-section">
            <div class="section-header">
              <span class="material-symbols-outlined">front_hand</span>
              <h4>Your Two Hands</h4>
            </div>
            <p class="intro-text">Each player is dealt <strong>two separate hands of 11 cards each</strong>:</p>
            <div class="hands-grid">
              <div class="hand-card main-hand">
                <div class="hand-header">
                  <span class="material-symbols-outlined">playing_cards</span>
                  <h5>Main Hand</h5>
                </div>
                <p>The cards you see and play first. Use these to start building melds with your team.</p>
              </div>
              <div class="hand-card foot-hand">
                <div class="hand-header">
                  <span class="material-symbols-outlined">visibility_off</span>
                  <h5>The Foot (Second Hand)</h5>
                </div>
                <p>11 cards dealt face-down. You cannot look at these until your Main Hand is completely empty!</p>
              </div>
            </div>
            <div class="foot-rules">
              <h5>When do you pick up your Foot?</h5>
              <ul>
                <li><strong>Meld ALL cards</strong> from your hand (no discard needed) → Pick up your Foot and <em>continue playing</em> the same turn!</li>
                <li><strong>Discard your last card</strong> → Turn ends. Pick up your Foot at the start of your <em>next</em> turn.</li>
              </ul>
            </div>
            <div class="warning-callout">
              <span class="material-symbols-outlined">warning</span>
              <div>
                <strong>Watch out for Red 3s!</strong>
                <p>Red 3s are penalty cards (-500 points each). They could be hiding in your Foot, and you cannot get rid of them!</p>
              </div>
            </div>
          </div>

          <!-- How a Turn Works -->
          <div class="overview-section turn-section">
            <div class="section-header">
              <span class="material-symbols-outlined">sync</span>
              <h4>How a Turn Works</h4>
            </div>
            <div class="turn-flow">
              <div class="turn-step draw-step">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h5>DRAW</h5>
                  <p>Take 2 cards from the stock pile</p>
                  <span class="step-alt">— OR —</span>
                  <p>Pick up the entire discard pile (requires 2 matching cards in hand)</p>
                </div>
              </div>
              <div class="turn-arrow">
                <span class="material-symbols-outlined">arrow_forward</span>
              </div>
              <div class="turn-step meld-step">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h5>MELD</h5>
                  <p>Play sets of cards to the table</p>
                  <span class="step-optional">(optional)</span>
                </div>
              </div>
              <div class="turn-arrow">
                <span class="material-symbols-outlined">arrow_forward</span>
              </div>
              <div class="turn-step discard-step">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h5>DISCARD</h5>
                  <p>Place 1 card on the discard pile</p>
                  <span class="step-required">(ends your turn)</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Melds and Canastas -->
          <div class="overview-section concepts-section">
            <div class="section-header">
              <span class="material-symbols-outlined">category</span>
              <h4>Melds and Canastas</h4>
            </div>
            <div class="concept-grid">
              <div class="concept-card">
                <h5>Meld</h5>
                <p>3 or more cards of the <strong>same rank</strong> laid on the table. Teams share melds — either partner can add to them!</p>
              </div>
              <div class="concept-card highlight">
                <h5>Canasta</h5>
                <p>A completed meld of <strong>exactly 7 cards</strong>. These are what you're trying to build!</p>
                <div class="canasta-types-mini">
                  <span class="clean-tag">Clean (no wilds) = 500 pts</span>
                  <span class="dirty-tag">Dirty (with wilds) = 300 pts</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Winning -->
          <div class="overview-section winning-section">
            <div class="section-header">
              <span class="material-symbols-outlined">emoji_events</span>
              <h4>How to Win</h4>
            </div>
            <div class="win-requirements">
              <h5>To "Go Out" and end the round, your team needs:</h5>
              <div class="requirements-list">
                <div class="req-item">
                  <span class="material-symbols-outlined">check_circle</span>
                  <span>2 Clean Canastas (7 cards, no wild cards)</span>
                </div>
                <div class="req-item">
                  <span class="material-symbols-outlined">check_circle</span>
                  <span>2 Dirty Canastas (7 cards, with wild cards)</span>
                </div>
                <div class="req-item">
                  <span class="material-symbols-outlined">check_circle</span>
                  <span>Empty both your Hand AND Foot</span>
                </div>
              </div>
            </div>
            <div class="win-goal">
              <span class="material-symbols-outlined">military_tech</span>
              <p>First team to reach <strong>10,000 points</strong> wins the game!</p>
            </div>
          </div>
        </div>

        <!-- Card Values Tab -->
        <div v-if="activeTab === 'cards'" class="tab-panel">
          <h3 class="panel-title">Card Values & Types</h3>
          <div class="card-table">
            <div class="card-category wild">
              <h4>Wild Cards</h4>
              <div class="card-row">
                <span class="card-name">Jokers</span>
                <span class="card-value">50 points</span>
              </div>
              <div class="card-row">
                <span class="card-name">2s (Deuces)</span>
                <span class="card-value">20 points</span>
              </div>
              <p class="card-note">Can substitute for any natural card</p>
            </div>
            <div class="card-category natural">
              <h4>Natural Cards</h4>
              <div class="card-row">
                <span class="card-name">Aces</span>
                <span class="card-value">20 points</span>
              </div>
              <div class="card-row">
                <span class="card-name">K-8</span>
                <span class="card-value">10 points</span>
              </div>
              <div class="card-row">
                <span class="card-name">7-4</span>
                <span class="card-value">5 points</span>
              </div>
            </div>
            <div class="card-category dead">
              <h4>Dead Cards</h4>
              <div class="card-row">
                <span class="card-name">Red 3s</span>
                <span class="card-value">-500 points</span>
              </div>
              <div class="card-row">
                <span class="card-name">Black 3s</span>
                <span class="card-value">-5 points</span>
              </div>
              <p class="card-note">Cannot be melded or played</p>
            </div>
          </div>
        </div>

        <!-- Melds Tab -->
        <div v-if="activeTab === 'melds'" class="tab-panel">
          <h3 class="panel-title">Meld Rules</h3>
          <div class="rule-section">
            <h4>Basic Requirements</h4>
            <ul>
              <li>Minimum 2 natural cards of the same rank</li>
              <li>Natural cards must outnumber wild cards</li>
              <li>Maximum 3 wild cards per meld (before becoming a canasta)</li>
              <li>Red 3s and Black 3s cannot be melded</li>
            </ul>
          </div>
          <div class="rule-section">
            <h4>Opening Meld Points</h4>
            <div class="threshold-table">
              <div class="threshold-row">
                <span>0 - 2,500</span>
                <span class="points">50 points</span>
              </div>
              <div class="threshold-row">
                <span>2,501 - 5,000</span>
                <span class="points">90 points</span>
              </div>
              <div class="threshold-row">
                <span>5,001 - 7,500</span>
                <span class="points">120 points</span>
              </div>
              <div class="threshold-row">
                <span>7,501 - 10,000</span>
                <span class="points">150 points</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Canastas Tab -->
        <div v-if="activeTab === 'canastas'" class="tab-panel">
          <h3 class="panel-title">Canastas (7+ cards)</h3>
          <div class="canasta-grid">
            <div class="canasta-type natural-canasta">
              <div class="canasta-header">
                <span class="material-symbols-outlined">stars</span>
                <h4>Clean Canasta</h4>
              </div>
              <p class="canasta-desc">7 cards, no wild cards</p>
              <p class="canasta-points">+500 points</p>
            </div>
            <div class="canasta-type mixed-canasta">
              <div class="canasta-header">
                <span class="material-symbols-outlined">shuffle</span>
                <h4>Dirty Canasta</h4>
              </div>
              <p class="canasta-desc">7 cards with wild cards</p>
              <p class="canasta-points">+300 points</p>
            </div>
            <div class="canasta-type wild-canasta">
              <div class="canasta-header">
                <span class="material-symbols-outlined">auto_awesome</span>
                <h4>Wild Canasta</h4>
              </div>
              <p class="canasta-desc">7 wild cards (2s & Jokers)</p>
              <p class="canasta-points">+1,000 points</p>
            </div>
            <div class="canasta-type hidden-canasta">
              <div class="canasta-header">
                <span class="material-symbols-outlined">visibility_off</span>
                <h4>Tagged Hidden Wild</h4>
              </div>
              <p class="canasta-desc">7 wilds built incrementally</p>
              <p class="canasta-points">+2,000 points</p>
            </div>
          </div>
        </div>

        <!-- Going Out Tab -->
        <div v-if="activeTab === 'going-out'" class="tab-panel">
          <h3 class="panel-title">Going Out Requirements</h3>
          <div class="requirements-checklist">
            <div class="requirement-item">
              <span class="material-symbols-outlined">check_circle</span>
              <span>2 Clean Canastas (7 cards each, no wilds)</span>
            </div>
            <div class="requirement-item">
              <span class="material-symbols-outlined">check_circle</span>
              <span>2 Dirty Canastas (7 cards each, with wilds)</span>
            </div>
            <div class="requirement-item">
              <span class="material-symbols-outlined">check_circle</span>
              <span>Empty hand and foot (all cards played or discarded)</span>
            </div>
            <div class="requirement-item">
              <span class="material-symbols-outlined">check_circle</span>
              <span>No hidden canasta (must be played before going out)</span>
            </div>
          </div>
          <div class="rule-section">
            <h4>Going Out Bonus</h4>
            <p>Team receives +100 points for going out first.</p>
          </div>
        </div>

        <!-- Scoring Tab -->
        <div v-if="activeTab === 'scoring'" class="tab-panel">
          <h3 class="panel-title">Scoring</h3>
          <div class="rule-section">
            <h4>Positive Points</h4>
            <ul>
              <li>Card values in melds/canastas</li>
              <li>Canasta bonuses (see Canastas tab)</li>
              <li>Going out bonus (+100)</li>
            </ul>
          </div>
          <div class="rule-section">
            <h4>Penalties</h4>
            <ul>
              <li>Red 3s: -500 points each</li>
              <li>Cards remaining in hand/foot when opponent goes out: -5 points each</li>
            </ul>
          </div>
          <div class="rule-section">
            <h4>Winning</h4>
            <p>First team to reach 10,000 points wins the game!</p>
          </div>
        </div>
      </div>

      <!-- Close Button -->
      <button @click="handleClose" class="btn-close">
        <span class="material-symbols-outlined">check_circle</span>
        <span>Got It</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * HowToPlayModal Component
 *
 * Comprehensive game rules and instructions for Canasta.
 * Tabbed interface covering all aspects of gameplay.
 *
 * Emits:
 * - close: [] - Modal closed
 */

interface Props {
  isOpen: boolean
  data?: null
}

defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const activeTab = ref('overview')

const tabs = [
  { id: 'overview', label: 'Overview', icon: 'info' },
  { id: 'cards', label: 'Card Values', icon: 'playing_cards' },
  { id: 'melds', label: 'Melds', icon: 'category' },
  { id: 'canastas', label: 'Canastas', icon: 'stars' },
  { id: 'going-out', label: 'Going Out', icon: 'exit_to_app' },
  { id: 'scoring', label: 'Scoring', icon: 'calculate' },
]

function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
.how-to-play-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Tab Navigation */
.tab-nav {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
}

.tab-button {
  flex: 1;
  min-width: 120px;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.tab-button.active {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(212, 175, 55, 0.2));
  border-color: #e8c547;
  color: #e8c547;
}

.tab-button .material-symbols-outlined {
  font-size: 1.125rem;
}

/* Tab Content */
.tab-content {
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

/* Custom Scrollbar */
.tab-content::-webkit-scrollbar {
  width: 8px;
}

.tab-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.tab-content::-webkit-scrollbar-thumb {
  background: rgba(232, 197, 71, 0.3);
  border-radius: 4px;
}

.tab-content::-webkit-scrollbar-thumb:hover {
  background: rgba(232, 197, 71, 0.5);
}

.tab-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.panel-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #e8c547;
  margin: 0;
}

.rule-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rule-section h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.rule-section p {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin: 0;
}

.rule-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.rule-section li {
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

.rule-section li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: #e8c547;
  font-weight: 600;
}

/* Card Table */
.card-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.card-category {
  padding: 1.25rem;
  border-radius: 10px;
  border: 2px solid;
}

.card-category.wild {
  background: rgba(168, 85, 247, 0.1);
  border-color: rgba(168, 85, 247, 0.3);
}

.card-category.natural {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.card-category.dead {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.card-category h4 {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 1rem;
}

.card-category.wild h4 {
  color: #a855f7;
}

.card-category.natural h4 {
  color: #10b981;
}

.card-category.dead h4 {
  color: #f87171;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.9375rem;
}

.card-name {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.card-value {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
}

.card-note {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
}

/* Threshold Table */
.threshold-table {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.threshold-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1.125rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  font-size: 0.9375rem;
}

.threshold-row .points {
  color: #e8c547;
  font-weight: 700;
  font-size: 1.125rem;
}

/* Canasta Grid */
.canasta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.canasta-type {
  padding: 1.25rem;
  border-radius: 10px;
  border: 2px solid;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.canasta-type.natural-canasta {
  background: rgba(251, 146, 60, 0.1);
  border-color: rgba(251, 146, 60, 0.3);
}

.canasta-type.mixed-canasta {
  background: rgba(107, 114, 128, 0.1);
  border-color: rgba(107, 114, 128, 0.3);
}

.canasta-type.wild-canasta {
  background: rgba(168, 85, 247, 0.1);
  border-color: rgba(168, 85, 247, 0.3);
}

.canasta-type.hidden-canasta {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.canasta-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.canasta-header .material-symbols-outlined {
  font-size: 1.5rem;
}

.canasta-type.natural-canasta .material-symbols-outlined {
  color: #fb923c;
}

.canasta-type.mixed-canasta .material-symbols-outlined {
  color: #6b7280;
}

.canasta-type.wild-canasta .material-symbols-outlined {
  color: #a855f7;
}

.canasta-type.hidden-canasta .material-symbols-outlined {
  color: #f87171;
}

.canasta-header h4 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.canasta-type.natural-canasta h4 {
  color: #fb923c;
}

.canasta-type.mixed-canasta h4 {
  color: #9ca3af;
}

.canasta-type.wild-canasta h4 {
  color: #a855f7;
}

.canasta-type.hidden-canasta h4 {
  color: #f87171;
}

.canasta-desc {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.canasta-points {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.canasta-type.natural-canasta .canasta-points {
  color: #fb923c;
}

.canasta-type.mixed-canasta .canasta-points {
  color: #9ca3af;
}

.canasta-type.wild-canasta .canasta-points,
.canasta-type.hidden-canasta .canasta-points {
  color: #a855f7;
}

/* Requirements Checklist */
.requirements-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 10px;
}

.requirement-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.requirement-item .material-symbols-outlined {
  font-size: 1.25rem;
  color: #10b981;
  flex-shrink: 0;
}

/* Close Button */
.btn-close {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
  align-self: center;
  max-width: 200px;
  width: auto;
}

.btn-close:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-close:active {
  transform: translateY(0);
}

.btn-close .material-symbols-outlined {
  font-size: 1.25rem;
}

/* ==========================================
   Overview Tab - Enhanced Styles
   ========================================== */

/* Overview Sections */
.overview-section {
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
}

.section-header .material-symbols-outlined {
  font-size: 1.5rem;
  color: #e8c547;
}

.section-header h4 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #e8c547;
  margin: 0;
}

.intro-text {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem;
  line-height: 1.6;
}

/* The Basics Section */
.basics-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.basic-item {
  flex: 1 1 45%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  box-sizing: border-box;
}

.basic-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5);
}

.basic-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

/* Your Two Hands Section */
.hands-grid {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.hand-card {
  flex: 1 1 0;
  min-width: 0;
  max-width: calc(50% - 5px);
  padding: 1rem;
  border-radius: 10px;
  border: 2px solid;
  box-sizing: border-box;
}

.hand-card.main-hand {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.hand-card.foot-hand {
  background: rgba(168, 85, 247, 0.1);
  border-color: rgba(168, 85, 247, 0.3);
}

.hand-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.hand-header .material-symbols-outlined {
  font-size: 1.25rem;
}

.hand-card.main-hand .material-symbols-outlined {
  color: #10b981;
}

.hand-card.foot-hand .material-symbols-outlined {
  color: #a855f7;
}

.hand-header h5 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.hand-card.main-hand h5 {
  color: #10b981;
}

.hand-card.foot-hand h5 {
  color: #a855f7;
}

.hand-card p {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.foot-rules {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.foot-rules h5 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.75rem;
}

.foot-rules ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.foot-rules li {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
  padding-left: 1.25rem;
  position: relative;
}

.foot-rules li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #e8c547;
  font-weight: bold;
}

/* Warning Callout */
.warning-callout {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
}

.warning-callout > .material-symbols-outlined {
  font-size: 1.5rem;
  color: #f87171;
  flex-shrink: 0;
}

.warning-callout strong {
  display: block;
  font-size: 0.9375rem;
  color: #f87171;
  margin-bottom: 0.25rem;
}

.warning-callout p {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 0;
}

/* Turn Flow Section */
.turn-flow {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem 0;
}

.turn-step {
  flex: 1;
  min-width: 140px;
  padding: 1rem;
  border-radius: 10px;
  border: 2px solid;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.turn-step.draw-step {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
}

.turn-step.meld-step {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.turn-step.discard-step {
  background: rgba(251, 146, 60, 0.1);
  border-color: rgba(251, 146, 60, 0.3);
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
}

.draw-step .step-number {
  background: rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.meld-step .step-number {
  background: rgba(16, 185, 129, 0.3);
  color: #34d399;
}

.discard-step .step-number {
  background: rgba(251, 146, 60, 0.3);
  color: #fb923c;
}

.step-content h5 {
  font-size: 0.9375rem;
  font-weight: 700;
  margin: 0;
}

.draw-step h5 {
  color: #60a5fa;
}

.meld-step h5 {
  color: #34d399;
}

.discard-step h5 {
  color: #fb923c;
}

.step-content p {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
  margin: 0;
}

.step-alt {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

.step-optional {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

.step-required {
  font-size: 0.75rem;
  color: #fb923c;
  font-weight: 500;
}

.turn-arrow {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.3);
}

.turn-arrow .material-symbols-outlined {
  font-size: 1.5rem;
}

/* Concepts Section */
.concept-grid {
  display: flex;
  gap: 1rem;
}

.concept-card {
  flex: 1;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
}

.concept-card.highlight {
  background: rgba(232, 197, 71, 0.1);
  border-color: rgba(232, 197, 71, 0.3);
}

.concept-card h5 {
  font-size: 1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.5rem;
}

.concept-card.highlight h5 {
  color: #e8c547;
}

.concept-card p {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 0;
}

.canasta-types-mini {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.75rem;
}

.clean-tag,
.dirty-tag {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.clean-tag {
  background: rgba(251, 146, 60, 0.2);
  color: #fb923c;
}

.dirty-tag {
  background: rgba(107, 114, 128, 0.2);
  color: #9ca3af;
}

/* Winning Section */
.win-requirements {
  margin-bottom: 1rem;
}

.win-requirements h5 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.75rem;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.req-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
}

.req-item .material-symbols-outlined {
  font-size: 1.125rem;
  color: #10b981;
}

.win-goal {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.15), rgba(212, 175, 55, 0.1));
  border: 1px solid rgba(232, 197, 71, 0.3);
  border-radius: 10px;
}

.win-goal .material-symbols-outlined {
  font-size: 2rem;
  color: #e8c547;
}

.win-goal p {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  .tab-nav {
    justify-content: center;
  }

  .tab-button {
    min-width: 100px;
  }

  .canasta-grid {
    grid-template-columns: 1fr;
  }

  .basics-grid,
  .hands-grid,
  .concept-grid {
    flex-direction: column;
  }

  .basic-item {
    flex: 1 1 100%;
  }

  .turn-flow {
    flex-direction: column;
  }

  .turn-arrow {
    justify-content: center;
    transform: rotate(90deg);
  }
}
</style>
